import moment from 'moment-timezone';
import { get } from 'lodash';
import { numberUnmask } from 'utils/unmask';
import { removeSpace } from 'utils/masks';
import { getOrConvertToPst } from 'utils/timezone';

export const getFirstDraftDateBounds = () => {
  const today = getOrConvertToPst().startOf('day');
  return {
    lowBound: today.clone().add(35, 'days'),
    upBound: today.clone().add(45, 'days'),
  };
};

export const toPstCalendarDay = (date) => {
  if (!date || typeof date.format !== 'function') {
    return null;
  }

  return moment.tz(
    {
      year: date.year(),
      month: date.month(),
      date: date.date(),
    },
    'America/Los_Angeles'
  ).startOf('day');
};

export const isFirstDraftDateInRange = (date) => {
  const currentDate = toPstCalendarDay(date);
  if (!currentDate) {
    return false;
  }

  const { lowBound, upBound } = getFirstDraftDateBounds();
  return currentDate.isSameOrAfter(lowBound, 'day') && currentDate.isSameOrBefore(upBound, 'day');
};

const validateFirstDraftDateInput = (input, formData) => {
  if (get(formData, 'isSelfSubmitted') !== 1 || get(formData, 'autopayFrequency') !== 'monthly') {
    return true;
  }

  if (!input) {
    return 'The input field is required';
  }

  if (numberUnmask(input).length < 8) {
    return 'First Draft Date is not valid';
  }

  if (!moment(input, 'MM/DD/YYYY').isValid()) {
    return 'First Draft Date is not valid';
  }

  const { lowBound, upBound } = getFirstDraftDateBounds();
  const inputDate = moment.tz(input, 'MM/DD/YYYY', 'America/Los_Angeles').startOf('day');

  if (inputDate.isBefore(lowBound, 'day') || inputDate.isAfter(upBound, 'day')) {
    return `First Draft Date must be between ${lowBound.format('MM/DD/YYYY')} and ${upBound.format('MM/DD/YYYY')}.`;
  }

  return true;
};

export default {
  bankName: [{
    rule: 'required',
    isValid: input => !!input || 'The input field is required',
  }],
  accountHolderName: [{
    rule: 'required',
    isValid: (input) => {
      if (!input || removeSpace(input).length === 0) {
        return 'The input field is required';
      }

      if (input.length > 40) {
        return 'The input field is too long';
      }

      if (input.trim().split(' ').length < 2) {
        return 'Please enter your full name';
      }

      if (!/^[a-zA-Z\s-']+$/.test(input)) {
        return 'Please enter a valid name, with only letters, - and \'.';
      }

      return true;
    },
  }],
  routingNumber: [
    {
      rule: 'required',
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }

        if (`${input}`.replace('_', '').length < 9) {
          return 'Routing Number is not valid';
        }

        return /^(\d)\1+$/.test(input) ? 'Routing Number is not valid' : true;
      },
    },
  ],
  accountNumber: [
    {
      rule: 'required',
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }

        return /^(\d)\1+$/.test(input) ? 'Account Number is not valid' : true;
      },
    },
  ],
  accountType: [{
    rule: 'required',
    error: 'The input field is required',
  }],
  firstPaymentDate: [{
    rule: 'required',
    isValid: (input) => {
      if (!input) {
        return 'The input field is required';
      }

      if (numberUnmask(input).length < 8) {
        return 'First Payment Date is not valid';
      }

      if (!moment(input, 'MM/DD/YYYY').isValid()) {
        return 'First Payment Date is not valid';
      }

      const lowBound = getOrConvertToPst().add(35, 'd');
      const upBound = getOrConvertToPst().add(45, 'd');
      const inputDate = moment.tz(input, 'MM/DD/YYYY', 'America/Los_Angeles');

      if (inputDate.isBefore(lowBound, 'day') || inputDate.isAfter(upBound, 'day')) {
        return `First Payment Date must be between ${lowBound.format('MM/DD/YYYY')} and ${upBound.format('MM/DD/YYYY')}.`;
      }
      return true;
    },
  }],
  isSelfSubmitted: [
    {
      rule: 'required',
      isValid: (input) => {
        if (!input) {
          return 'The input field is required';
        }
        return true;
      },
    },
  ],
  autopayFrequency: [{
    rule: 'required',
    isValid: (input, err, opt, formData) => {
      if (get(formData, 'isSelfSubmitted') !== 1) {
        return true;
      }

      return !!input || 'The input field is required';
    },
  }],
  firstDraftDate: [{
    rule: 'required',
    isValid: (input, err, opt, formData) => validateFirstDraftDateInput(input, formData),
  }],
  draftDay: [{
    rule: 'required',
    isValid: (input, err, opt, formData) => {
      if (get(formData, 'isSelfSubmitted') !== 1 || get(formData, 'autopayFrequency') === 'monthly') {
        return true;
      }

      return !!input || 'The input field is required';
    },
  }],
};
